/**
 * CollapsibleSection Component
 *
 * A section header + `forge-expansion-panel` pairing that follows the WAI-ARIA
 * Accordion authoring pattern: the toggle `forge-button` lives *inside* the
 * heading (`<h2><button>Title</button></h2>`), not the other way around, so
 * the accessible name of the heading matches the visible title and the
 * button remains valid phrasing content. Expand/collapse state is owned by
 * React (not the Forge component's internal id-based trigger wiring) so it
 * behaves identically in real Forge and in the mocked-custom-element unit
 * test environment (see issue #498).
 */

import { useState, type ReactNode } from 'react';
import '@tylertech/forge/expansion-panel';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './CollapsibleSection.css';

export interface CollapsibleSectionProps {
  /** Id applied to the <h2>; the panel's aria-labelledby points back at it. */
  headingId: string;
  /** Section title text rendered inside the toggle button. */
  title: string;
  /** Initial expanded state. Defaults to expanded. */
  defaultOpen?: boolean;
  /** Optional action(s) rendered alongside the toggle (e.g. an "Add" button). */
  actions?: ReactNode;
  /** Base data-testid; toggle gets `${testId}-toggle`, panel gets `${testId}-panel`. */
  testId?: string;
  children: ReactNode;
}

const ForgeButton = 'forge-button' as React.ElementType;
const ForgeExpansionPanel = 'forge-expansion-panel' as React.ElementType;
const ForgeIcon = 'forge-icon' as React.ElementType;

export function CollapsibleSection({
  headingId,
  title,
  defaultOpen = true,
  actions,
  testId,
  children,
}: CollapsibleSectionProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `${headingId}-panel`;

  return (
    <>
      <div className="project-detail-section-header">
        <h2 id={headingId} className="forge-typography--headline6 collapsible-section-heading">
          <ForgeButton
            type="button"
            variant="text"
            className="collapsible-section-trigger"
            aria-expanded={open ? 'true' : 'false'}
            aria-controls={panelId}
            onClick={() => setOpen((prev) => !prev)}
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <ForgeIcon
              name={open ? 'expand_less' : 'expand_more'}
              slot="start"
              aria-hidden="true"
            />
            {title}
          </ForgeButton>
        </h2>
        {actions}
      </div>
      <ForgeExpansionPanel
        id={panelId}
        open={open}
        data-testid={testId ? `${testId}-panel` : undefined}
      >
        {children}
      </ForgeExpansionPanel>
    </>
  );
}
